import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhStop";
import "@phosphor-icons/webcomponents/PhBroadcast";
import "@phosphor-icons/webcomponents/PhExport";
import "@phosphor-icons/webcomponents/PhDotsThreeVertical";
import "@phosphor-icons/webcomponents/PhNotePencil";
import "@phosphor-icons/webcomponents/PhCopy";
import "@phosphor-icons/webcomponents/PhTrash";
import "@phosphor-icons/webcomponents/PhEye";
import "@phosphor-icons/webcomponents/PhVideoCamera";
import "ui-components/lucide-icons/share";
import "ui-components/button/wow-button";
import "ui-components/dropdown-menu/dropdown-menu";
import "ui-components/dropdown-menu/dropdown-menu-item";
import "ui-components/status-badge/status-badge";
import "ui-components/image-or-video/image-or-video";

import { getAssetUrl } from "helpers/assetHelpers";
import type { LiveEvent } from "api-contracts/rpc/get-live-events";
import type { LiveEventStatus } from "ui-common/services/live-event-state/types";
import { t } from "../../translations/i18n";
import { styles } from "./upcoming-event-banner.css";

@customElement("upcoming-event-banner")
export class UpcomingEventBanner extends LitElement {
  static override styles = [styles];

  @property({ type: Object })
  event!: LiveEvent;

  private emit(name: string, detail?: unknown) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  private handleStartStreamClick() {
    this.emit("stream-action", { action: "browser", eventId: this.event.id });
  }

  private handleMenuAction(e: CustomEvent) {
    const action = e.detail?.action;
    switch (action) {
      case "share":
        this.handleShareClick();
        break;
      case "edit":
        this.emit("edit-event", { event: this.event });
        break;
      case "duplicate":
        this.emit("duplicate-event", { event: this.event });
        break;
      case "watch":
        this.emit("watch-event", { event: this.event });
        break;
      case "end-event":
        this.handleEndEventClick();
        break;
      case "delete":
        this.emit("delete-event", { eventId: this.event.id });
        break;
      case "archive":
        this.emit("archive-event", { eventId: this.event.id });
        break;
      case "start-streaming":
        this.handleStartStreamClick();
        break;
    }
  }

  private handleShareClick() {
    this.emit("share-click", { event: this.event });
  }

  private handleEndEventClick() {
    this.emit("end-event", { eventId: this.event.id });
  }

  private canEndEvent(): boolean {
    if (this.event.status === "live") return true;
    if (this.event.status === "scheduled") {
      return new Date(this.event.scheduled_start_time).getTime() <= Date.now();
    }
    return false;
  }

  private renderStatusBadge() {
    const status = this.event.status as LiveEventStatus;
    const labelKey: Record<LiveEventStatus, Parameters<typeof t>[0]> = {
      scheduled: "liveEventsPage.scheduled",
      live: "liveEventsPage.live",
      ended: "liveEventsPage.ended",
      cancelled: "liveEventsPage.cancelled",
      archived: "liveEventsPage.archived",
    };
    const variant = status === "live" ? "live" : "event";
    return html`
      <status-badge variant=${variant} size="medium" ?pulse=${status === "live"}> ${t(labelKey[status])} </status-badge>
    `;
  }

  private formatRevenue(cents: number): string {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "EUR",
    }).format(cents / 100);
  }

  override render() {
    const status = this.event.status;
    const startDate = new Date(this.event.scheduled_start_time);

    return html`
      <div class="upcoming-banner ${status === "live" ? "is-live" : ""}">
        ${this.event.thumbnail_url
          ? html`
              <image-or-video
                class="upcoming-banner-thumbnail"
                url=${getAssetUrl(this.event.thumbnail_url)}
                alt="${this.event.title}"
                @click=${() => this.emit("edit-event", { event: this.event })}
              ></image-or-video>
            `
          : nothing}
        <div class="upcoming-banner-content">
          <div class="upcoming-banner-header">
            <h3 @click=${() => this.emit("edit-event", { event: this.event })}>${this.event.title}</h3>
            ${status === "live" ? this.renderStatusBadge() : nothing}
          </div>
          <div class="upcoming-banner-bottom">
            <div class="upcoming-banner-info">
              <div class="upcoming-banner-date-row">
                ${status !== "live" ? this.renderStatusBadge() : nothing}
                <div class="upcoming-banner-stat">
                  <span class="stat-label">${t("liveEventsPage.columnDate")}</span>
                  <span class="stat-value"
                    >${startDate.toLocaleDateString(undefined, { dateStyle: "long" }).toLowerCase()} ·
                    ${startDate.toLocaleTimeString(undefined, { timeStyle: "short" })}</span
                  >
                </div>
              </div>
              <div class="upcoming-banner-stats">
                ${this.event.fee_cents
                  ? html`
                      <div class="upcoming-banner-stat">
                        <span class="stat-label">${t("liveEventsPage.columnTicketPrice")}</span>
                        <span class="stat-value">&euro;${(this.event.fee_cents / 100).toFixed(2)}</span>
                      </div>
                    `
                  : nothing}
                <div class="upcoming-banner-stat">
                  <span class="stat-label">${t("liveEventsPage.columnTicketsSold")}</span>
                  <span class="stat-value">${this.event.tickets_sold}</span>
                </div>
                <div class="upcoming-banner-stat">
                  <span class="stat-label">${t("liveEventsPage.columnRevenue")}</span>
                  <span class="stat-value">${this.formatRevenue(this.event.revenue_cents)}</span>
                </div>
              </div>
            </div>
            <div class="upcoming-banner-stream-actions">
              ${status === "scheduled"
                ? html`
                    <wow-button variant="primary" size="medium" @click=${this.handleStartStreamClick}>
                      ${t("liveEventsPage.startStreaming")}
                    </wow-button>
                  `
                : nothing}
              ${status === "live"
                ? html`
                    <wow-button variant="neutral" size="small" @click=${this.handleEndEventClick}>
                      <ph-stop size="16" weight="fill"></ph-stop>
                      ${t("liveEventsPage.endLiveEvent")}
                    </wow-button>
                    <wow-button variant="danger" size="small" @click=${this.handleStartStreamClick}>
                      <ph-broadcast size="16" weight="fill"></ph-broadcast>
                      ${t("liveEventsPage.returnToLivestream")}
                    </wow-button>
                  `
                : nothing}
              <wow-button
                class="share-button-standalone"
                variant="subtle"
                size="small"
                square
                .borderColor=${"var(--sds-color-border-default-secondary)"}
                @click=${this.handleShareClick}
                aria-label="Share event"
              >
                <lu-share size="20"></lu-share>
              </wow-button>
              <dropdown-menu placement="bottom-end" @dropdown-item-click=${this.handleMenuAction}>
                <wow-button
                  slot="trigger"
                  variant="subtle"
                  size="small"
                  square
                  .borderColor=${"var(--sds-color-border-default-secondary)"}
                  aria-label="More options"
                >
                  <ph-dots-three-vertical size="20"></ph-dots-three-vertical>
                </wow-button>
                ${status === "live"
                  ? html`
                      <dropdown-menu-item action="start-streaming" icon-position="start">
                        <ph-video-camera slot="icon" size="16"></ph-video-camera>
                        ${t("liveEventsPage.startStreaming")}
                      </dropdown-menu-item>
                    `
                  : nothing}
                <dropdown-menu-item action="edit" icon-position="start">
                  <ph-note-pencil slot="icon" size="16"></ph-note-pencil>
                  ${t("liveEventsPage.editEvent")}
                </dropdown-menu-item>
                <dropdown-menu-item class="share-menu-item" action="share" icon-position="start">
                  <ph-export slot="icon" size="16"></ph-export>
                  ${t("liveEventsPage.shareEvent")}
                </dropdown-menu-item>
                <dropdown-menu-item action="duplicate" icon-position="start">
                  <ph-copy slot="icon" size="16"></ph-copy>
                  ${t("liveEventsPage.duplicateEvent")}
                </dropdown-menu-item>
                <dropdown-menu-item action="watch" icon-position="start">
                  <ph-eye slot="icon" size="16"></ph-eye>
                  ${t("liveEventsPage.watchEvent")}
                </dropdown-menu-item>
                ${this.canEndEvent()
                  ? html`
                      <dropdown-menu-item action="end-event" variant="danger" icon-position="start">
                        <ph-stop slot="icon" size="16" weight="fill"></ph-stop>
                        ${t("liveEventsPage.endEvent")}
                      </dropdown-menu-item>
                    `
                  : nothing}
                ${status !== "live" && status !== "archived"
                  ? status === "scheduled" && this.event.tickets_sold === 0
                    ? html`
                        <dropdown-menu-item action="delete" variant="danger" icon-position="start">
                          <ph-trash slot="icon" size="16"></ph-trash>
                          ${t("liveEventsPage.deleteEvent")}
                        </dropdown-menu-item>
                      `
                    : html`
                        <dropdown-menu-item action="archive" variant="danger" icon-position="start">
                          <ph-trash slot="icon" size="16"></ph-trash>
                          ${t("liveEventsPage.archiveEvent")}
                        </dropdown-menu-item>
                      `
                  : nothing}
              </dropdown-menu>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "upcoming-event-banner": UpcomingEventBanner;
  }
}
